import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SessionBooking,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

import ClientTag from "../../components/clientTag/ClientTag"
import { useSendbird } from "../../components/sendbird/SBProvider"
import useCoachingGroup from "../../hooks/useCoachingGroup"
import useMobileViewStore, {
  SendBirdView,
} from "../../pages/home/store/useMobileViewStore"
import { CoachingGroup } from "../../types"
import { formatDate, getRelativeDateTime } from "../../util/dates"
import { checkCoachMessageAlerts } from "../../util/sendbird"
interface ClientListItemProps {
  coachingGroup: CoachingGroup
  nextSession?: SessionBooking | undefined
  isUpcomingSession?: boolean
}

const ClientListItem = ({
  nextSession,
  coachingGroup,
  isUpcomingSession = false,
}: ClientListItemProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const {
    currentChannel,
    markAsRead,
    scrollToBottomOfMessages,
    getChannelByUrl,
  } = useSendbird()
  const { setView: setMobileView } = useMobileViewStore()
  const channel = getChannelByUrl(coachingGroup?.sendbird_channel_url)
  const { client } = useCoachingGroup(coachingGroup)
  const navigate = useNavigate()

  if (!client || !channel) return null

  const unreadMessages =
    channel.unreadMessageCount + channel.totalUnreadReplyCount
  const isSelected = channel.url === currentChannel?.url

  const lastMessageSender = coachingGroup?.profiles.find(
    // @ts-ignore - sender is on lastMessage
    (p) => p.id === Number(channel.lastMessage?.sender?.userId)
  )

  const { showCoachMessageAlert, showClientMessageAlert, messageDaysAgo } =
    checkCoachMessageAlerts(lastMessageSender, channel.lastMessage?.createdAt)

  const onSelectChannel = () => {
    setMobileView(SendBirdView.CHANNEL_DETAIL)
    navigate(`/${coachingGroup.id}/messages`)

    if (channel.unreadMessageCount > 0) {
      setTimeout(() => {
        scrollToBottomOfMessages()
        markAsRead(channel.url)
      }, 250)
    }
  }

  const isHappeningNow =
    dayjs(nextSession?.start_datetime).isBefore(dayjs()) &&
    dayjs(nextSession?.end_datetime).isAfter(dayjs())

  return (
    <button
      onClick={onSelectChannel}
      style={{ width: "100%", position: "relative" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FlexBox
        direction="column"
        style={{
          padding: `${SPACING.space3} ${SPACING.space4}`,
          cursor: "pointer",
          borderBottom: `1px solid ${JoonUIColor.border.default}`,
          textAlign: "left",
          whiteSpace: "nowrap",
          backgroundColor: isSelected
            ? JoonUIColor.background.lightBlue
            : isHovered
            ? JoonUIColor.background.xlightGray
            : "transparent",
        }}
        gap={SPACING.space2}
      >
        {isUpcomingSession && !!nextSession && (
          <Typography
            variant="bodyXSmallBold"
            style={{
              background: JoonColorExpanded.yellow200,
              padding: `${SPACING.space1} ${SPACING.space2}`,
              borderRadius: SPACING.space2,
              width: "fit-content",
            }}
          >
            {isHappeningNow ? (
              "Happening now..."
            ) : (
              <>
                {formatDate(
                  new Date(nextSession?.start_datetime ?? ""),
                  "MMM D"
                )}
                ,{" "}
                {formatDate(
                  new Date(nextSession?.start_datetime ?? ""),
                  " h:mm A",
                  false
                )}
              </>
            )}
          </Typography>
        )}
        <FlexBox
          direction="column"
          gap={SPACING.space1}
          style={{ flex: 1, minWidth: 0 }}
        >
          <FlexBox
            direction="row"
            gap={SPACING.space2}
            wrap={false}
            justify="space-between"
            align="center"
          >
            <ClientTag coachingGroup={coachingGroup} />
            <Typography
              variant="bodyBold"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {client.user.name}{" "}
              {client.user.last_name ? client.user.last_name : ""}
            </Typography>
            {channel.lastMessage?.createdAt && (
              <Typography
                variant="bodyXSmall"
                color={JoonUIColor.text.disabled}
              >
                {getRelativeDateTime(new Date(channel.lastMessage?.createdAt))}
              </Typography>
            )}
          </FlexBox>
          <FlexBox
            direction="row"
            gap={SPACING.space2}
            wrap={false}
            justify="space-between"
          >
            <Typography
              variant="bodyXSmall"
              textAlign="left"
              color={JoonUIColor.text.disabled}
              style={{
                maxWidth: "calc(100% - 30px)",
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
              }}
            >
              {channel.lastMessage?.message || "No messages yet!"}
            </Typography>

            {unreadMessages > 0 && (
              <FlexBox
                justify="center"
                align="center"
                style={{
                  minWidth: SPACING.space5,
                  width: "fit-content",
                  background: JoonUIColor.semantic.primary,
                  height: SPACING.space5,
                  maxHeight: SPACING.space5,
                  borderRadius: "50%",
                }}
              >
                <Typography
                  variant="bodyXSmall"
                  color={JoonUIColor.text.inverted}
                >
                  {unreadMessages}
                </Typography>
              </FlexBox>
            )}
          </FlexBox>
        </FlexBox>
        {showCoachMessageAlert && (
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.semantic.destructive}
          >
            {messageDaysAgo} days since the last message
          </Typography>
        )}
        {showClientMessageAlert && (
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.semantic.destructive}
          >
            Client is waiting for a response
          </Typography>
        )}
      </FlexBox>
    </button>
  )
}

export default ClientListItem
